import { useRef } from 'react'
import { batch } from 'react-redux'
import { buildCheckoutRequestItem } from '@ancon/wildcat-utils/checkout'
import { createCodedError } from '@ancon/wildcat-utils/error'
import { CheckoutItemType, ErrorCode } from '@ancon/wildcat-types'
import { CheckoutRequestItem } from '@ancon/wildcat-utils/checkout/types'

import {
  ConfigureProductFormState,
  ConfigureProductUpsellGroupFormState,
} from '../types'
import useAppSelector from '../../../store/hooks/useAppSelector'
import {
  productDetailsSelector,
  productsUpSellGroupIsOutOfStockSelector,
  productsUpsellGroupHasProductsSelector,
} from '../store/productSelectors'
import useAppDispatch from '../../../store/hooks/useAppDispatch'
import {
  setProductDetailsModalVisible,
  setProductUpsellModalVisible,
} from '../store/productSlice'
import { checkoutCurrentCheckoutSelectedItemIdSelector } from '../../checkout/store/checkoutSelectors'
import useAppStore from '../../../store/hooks/useAppStore'
import {
  outletListItemSelector,
  outletSelectedOutletIdSelector,
} from '../../outlet/store/outletSelector'
import {
  addCheckoutItem,
  updateCheckoutItem,
} from '../../checkout/store/checkoutThunks'
import {
  checkoutRememberRequestedItemDetails,
  setSelectedCheckoutItemId,
} from '../../checkout/store/checkoutSlice'
import getCheckoutRequestedItemDetails from '../../checkout/utils/getCheckoutRequestedItemDetails'
import appLanguage from '../../app/utils/appLanguage'
import { appShowError } from '../../app/store/appSlice'
import { ErrorModalType } from '../../app/types'
import getTranslatedError from '../../app/utils/getTranslatedError'
import useRouteMatch from '../../app/hooks/useRouteMatch'
import { AppRoutes } from '../../app/constants'
import {
  isPreOrderCheckoutSelector,
  preOrderSelectedAttendeeIdSelector,
} from '../../preOrder/store/preOrderSelectors'
import {
  addPreOrderCheckoutItems,
  updatePreOrderCheckoutItem,
} from '../../preOrder/store/preOrderThunks'
import isOutletAvailableForOrders from '../../outlet/utils/isOutletAvailableForOrders'
import getSelectedUpsellCount from '../utils/getSelectedUpsellCount'

import ProductDetailsModal from './ProductDetailsModal'
import ProductUpsellModal from './ProductUpsellModal'

export default function OrderProduct() {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const hasUpsellGroups = useAppSelector(productsUpsellGroupHasProductsSelector)
  const productUpsellIsOutOfStock = useAppSelector(
    productsUpSellGroupIsOutOfStockSelector,
  )
  const outlet = useAppSelector(outletListItemSelector)

  const isPreOrderManage = useRouteMatch([AppRoutes.PreOrderManage])

  const productDetailsFormValuesRef = useRef<
    ConfigureProductFormState | undefined
  >(undefined)

  function clearSelectedCheckoutItemId() {
    const selectedCheckoutItemId =
      checkoutCurrentCheckoutSelectedItemIdSelector(store.getState())

    if (selectedCheckoutItemId) {
      dispatch(setSelectedCheckoutItemId(null))
    }
  }

  async function handleAddToItemsToCart(
    upsellFormState?: ConfigureProductUpsellGroupFormState,
  ) {
    const selectedOutletId = outletSelectedOutletIdSelector(store.getState())
    const selectedCheckoutItemId =
      checkoutCurrentCheckoutSelectedItemIdSelector(store.getState())
    const isPreOrderCheckout = isPreOrderCheckoutSelector(store.getState())
    const selectedAttendeeId = preOrderSelectedAttendeeIdSelector(
      store.getState(),
    )

    const productDetailsFormValuesState = productDetailsFormValuesRef.current
    // Cleared up front so a second close/submit while the request runs can't add it twice
    productDetailsFormValuesRef.current = undefined

    if (productDetailsFormValuesState) {
      const items: CheckoutRequestItem[] = []

      const { outletId, selectedVariantId, ...productDetailsFormValues } =
        productDetailsFormValuesState

      const checkoutRequestItem = buildCheckoutRequestItem({
        ...productDetailsFormValues,
        selectedVariantId: selectedVariantId!,
        outletId,
      })

      items.push(checkoutRequestItem)

      if (upsellFormState && upsellFormState.selectedUpsellGroups?.length) {
        const { selectedUpsellGroups, upsellGroupId } = upsellFormState
        const upsellSelectedProducts = selectedUpsellGroups.reduce<
          CheckoutRequestItem[]
        >((acc, product) => {
          if (product.selectedCount > 0) {
            acc.push({
              variantId: product.variantId,
              quantity: product.selectedCount,
              modifications: [],
              upsellGroupId,
              addOnProducts: [],
              itemType: CheckoutItemType.UpsellProduct,
            })
          }
          return acc
        }, [])

        items.push(...upsellSelectedProducts)
      }

      try {
        if (selectedCheckoutItemId) {
          if (isPreOrderCheckout) {
            await dispatch(
              updatePreOrderCheckoutItem({
                updatedItem: items[0],
                checkoutItemId: selectedCheckoutItemId,
                attendeeId: selectedAttendeeId!,
              }),
            )
          } else {
            await dispatch(
              updateCheckoutItem({
                item: items[0],
                checkoutItemId: selectedCheckoutItemId,
                outletId: outletId || selectedOutletId,
              }),
            )
          }
        } else if (isPreOrderCheckout) {
          await dispatch(
            addPreOrderCheckoutItems({
              items,
              attendeeId: selectedAttendeeId!,
            }),
          )
        } else {
          await dispatch(
            addCheckoutItem({
              items,
              outletId: outletId || selectedOutletId,
            }),
          )
        }
      } catch (err) {
        // TODO: handle error
      }

      clearSelectedCheckoutItemId()
    }
  }

  /**
   * The add request only carries variant ids, and the product details are cleared
   * the moment a product modal closes - which happens before the request is made.
   * So the display names are captured here, from both submit paths: the details
   * modal covers the product itself, the upsell modal covers what is picked there.
   */
  function rememberRequestedItemDetails(selectedVariantId: string | null) {
    const productDetails = productDetailsSelector(store.getState())

    if (productDetails) {
      dispatch(
        checkoutRememberRequestedItemDetails(
          getCheckoutRequestedItemDetails(productDetails, selectedVariantId),
        ),
      )
    }
  }

  async function handleProductDetailsModalSubmit(
    values: ConfigureProductFormState,
  ) {
    if (!isPreOrderManage && outlet && !isOutletAvailableForOrders(outlet)) {
      const locale = appLanguage.get()
      const error = await getTranslatedError(
        createCodedError(
          'Restaurant not open yet',
          ErrorCode.RequestedOutletIsNotOpen,
        ),
        undefined,
        locale,
      )
      dispatch(
        appShowError({
          ...error,
          errorModalType: ErrorModalType.OutletNotAvailableForOrders,
        }),
      )
      return
    }
    productDetailsFormValuesRef.current = values

    rememberRequestedItemDetails(values.selectedVariantId)

    const selectedCheckoutItemId =
      checkoutCurrentCheckoutSelectedItemIdSelector(store.getState())

    if (
      hasUpsellGroups &&
      !productUpsellIsOutOfStock &&
      !selectedCheckoutItemId
    ) {
      batch(() => {
        dispatch(setProductUpsellModalVisible(true))
        dispatch(setProductDetailsModalVisible(false))
      })
    } else {
      dispatch(setProductDetailsModalVisible(false))
      await handleAddToItemsToCart()
    }
  }

  async function closeUpsellModalAndAddToCart(
    upsellFormState?: ConfigureProductUpsellGroupFormState,
  ) {
    if (productDetailsFormValuesRef.current) {
      // Must run before the modal closes - that is what nulls the product details.
      rememberRequestedItemDetails(
        productDetailsFormValuesRef.current.selectedVariantId,
      )

      dispatch(setProductUpsellModalVisible(false))
      await handleAddToItemsToCart(upsellFormState)
    } else {
      dispatch(setProductUpsellModalVisible(false))
      clearSelectedCheckoutItemId()
    }
  }

  async function handleUpsellModalSubmit(
    values: ConfigureProductUpsellGroupFormState,
  ) {
    const selectedUpsellCount = getSelectedUpsellCount(
      values.selectedUpsellGroups,
    )
    const hasSelections = selectedUpsellCount > 0
    await closeUpsellModalAndAddToCart(hasSelections ? values : undefined)
  }

  function handleProductDetailsModalDismiss() {
    dispatch(setProductDetailsModalVisible(false))
    clearSelectedCheckoutItemId()
  }

  return (
    <>
      <ProductDetailsModal
        onSubmit={handleProductDetailsModalSubmit}
        onDismiss={handleProductDetailsModalDismiss}
      />
      <ProductUpsellModal onSubmit={handleUpsellModalSubmit} />
    </>
  )
}
